import React, { useEffect } from 'react';
import { Box, Autocomplete as MuiAutocomplete, Stack, TextField } from '@mui/material';
import { bool, func, shape } from 'prop-types';
import * as R from 'ramda';
import { useTheme } from '@mui/material/styles';
import { useUserRolePermissions } from 'hooks';
import {
  useGetCitiesAndZipsQuery,
  useGetClassesQuery,
  useGetStatesQuery,
} from '../../customerAuthorizationsEndpoints';
import AuthTypeAndVolumes from './AuthTypeAndVolumes';
import LocationAutocomplete from './LocationAutocomplete';
import { maxLengthError, requiredError } from './validators';

const defaultToEmpty = R.defaultTo('');

const CustomerInfoForm = ({
  customerAuth,
  isReadOnly,
  setCustomerAuth,
  setValidation,
  validation,
}) => {
  const { currentAffiliate } = useUserRolePermissions();
  const {
    address,
    address2,
    city,
    customerClassId,
    customerClassName,
    customerIdName,
    customerName,
    phoneNumber,
    state,
    zipCode,
  } = customerAuth;

  const { data: allStates, isLoading: isLoadingStates } = useGetStatesQuery();
  const states = R.map(({ id, name }) => ({ label: name, value: id }), allStates ?? []);
  const foundState = states.find(found => found.value === state);
  const theme = useTheme();

  const classParams = {
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };
  const { data: allClasses = [], isLoading: isLoadingClasses } = useGetClassesQuery(
    { params: classParams },
    { skip: R.isNil(currentAffiliate) }
  );
  const classOptions = R.map(
    c => ({ label: c.customerClassName, value: c.customerClassId }),
    allClasses
  );

  const { data: citiesZips, isLoading: isLoadingCityZip } = useGetCitiesAndZipsQuery(
    { params: { state: foundState?.label } },
    { skip: R.isNil(foundState) }
  );
  const cities = R.map(
    item => ({ label: item.city, value: item.city }),
    citiesZips?.citiesAndZips ?? []
  );
  const foundCity = citiesZips?.citiesAndZips?.find(item => item.city === city);
  const zips = R.map(zip => ({ label: zip, value: zip }), foundCity?.zips ?? []);

  useEffect(() => {
    setValidation(current => ({
      ...current,
      address: maxLengthError(address, 50),
      address2: maxLengthError(address2, 50),
      customerName: requiredError(customerName) || maxLengthError(customerName, 40),
      phoneNumber: maxLengthError(phoneNumber, 20),
    }));
  }, [address, address2, customerName, phoneNumber, setValidation]);

  const isClassMissing =
    R.not(R.isNil(customerClassId)) &&
    R.not(isLoadingClasses) &&
    R.isEmpty(classOptions.filter(o => o.value === customerClassId));
  useEffect(() => {
    if (isClassMissing) {
      setCustomerAuth(current =>
        R.evolve(
          {
            customerClassId: R.always(undefined),
            customerClassName: R.always(undefined),
          },
          current
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClassMissing]);

  const handleCustomerNameChange = e => {
    const { value } = e.target;
    if (value.length > 40) return;
    setCustomerAuth(R.assoc('customerName', value));
    setValidation(R.assoc('customerName', requiredError(value)));
  };

  const handleTextChange = (field, max) => e => {
    const { value } = e.target;
    setCustomerAuth(R.assoc(field, value));
    setValidation(R.assoc(field, maxLengthError(value, max)));
  };

  const handleStateChange = (_, option, reason) => {
    const value = reason === 'clear' ? undefined : option.value;
    setCustomerAuth(current =>
      R.evolve(
        {
          city: R.always(undefined),
          state: R.always(value),
          zipCode: R.always(undefined),
        },
        current
      )
    );
  };

  const handleCityChange = (_, option, reason) => {
    const value = reason === 'clear' ? undefined : option.value;
    setCustomerAuth(current =>
      R.evolve(
        {
          city: R.always(value),
          zipCode: R.always(undefined),
        },
        current
      )
    );
  };

  const handleZipChange = (_, option, reason) => {
    setCustomerAuth(R.assoc('zipCode', reason === 'clear' ? undefined : option.value));
  };

  const handleClassChange = (_, option, reason) => {
    setCustomerAuth(current =>
      R.evolve(
        {
          customerClassId: R.always(reason === 'clear' ? undefined : option.value),
          customerClassName: R.always(reason === 'clear' ? undefined : option.label),
        },
        current
      )
    );
  };

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        backgroundColor: theme.palette.surface.light,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        p: 2,
      }}
    >
      <AuthTypeAndVolumes
        customerAuth={customerAuth}
        isReadOnly={isReadOnly}
        setCustomerAuth={setCustomerAuth}
        setValidation={setValidation}
        validation={validation}
      />
      <TextField disabled label="Customer ID" value={defaultToEmpty(customerIdName)} />
      <TextField
        disabled={isReadOnly}
        error={!!validation.customerName}
        helperText={validation.customerName}
        label="Customer Name"
        onChange={handleCustomerNameChange}
        required
        value={defaultToEmpty(customerName)}
      />
      <TextField
        disabled={isReadOnly}
        error={!!validation.address}
        helperText={validation.address}
        label="Street Address"
        onChange={handleTextChange('address', 50)}
        value={defaultToEmpty(address)}
      />
      <TextField
        disabled={isReadOnly}
        error={!!validation.address2}
        helperText={validation.address2}
        label="Suite, Apt. #, PO Box"
        onChange={handleTextChange('address2', 50)}
        value={defaultToEmpty(address2)}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <LocationAutocomplete
          disabled={isReadOnly}
          id="state"
          label="State"
          loading={isLoadingStates}
          name="state"
          onChange={handleStateChange}
          options={states}
          value={state}
        />
        <LocationAutocomplete
          disabled={isReadOnly || R.isNil(state)}
          helperText={R.isNil(state) ? 'Please select state first' : ''}
          id="city"
          label="City"
          loading={isLoadingCityZip}
          name="city"
          onChange={handleCityChange}
          options={cities}
          value={city}
        />
        <LocationAutocomplete
          disabled={isReadOnly || R.isNil(city)}
          helperText={R.isNil(city) ? 'Please select state & city first' : ''}
          id="zipCode"
          label="Zip Code"
          loading={isLoadingCityZip}
          name="zipCode"
          onChange={handleZipChange}
          options={zips}
          value={zipCode}
        />
      </Box>
      <TextField
        disabled={isReadOnly}
        error={!!validation.phoneNumber}
        helperText={validation.phoneNumber}
        label="Phone Number"
        onChange={handleTextChange('phoneNumber', 20)}
        value={defaultToEmpty(phoneNumber)}
      />
      <MuiAutocomplete
        clearOnBlur={false}
        disabled={isReadOnly}
        getOptionLabel={o => defaultToEmpty(o.label)}
        isOptionEqualToValue={(o, selected) => o.value === selected.value}
        loading={isLoadingClasses}
        onChange={handleClassChange}
        options={classOptions}
        renderInput={p => <TextField {...p} label="Class" />}
        value={
          R.isNil(customerClassId) ? null : { label: customerClassName, value: customerClassId }
        }
      />
    </Stack>
  );
};

CustomerInfoForm.propTypes = {
  customerAuth: shape({}),
  isReadOnly: bool,
  setCustomerAuth: func.isRequired,
  setValidation: func.isRequired,
  validation: shape({}).isRequired,
};

CustomerInfoForm.defaultProps = {
  customerAuth: {},
  isReadOnly: false,
};

export default CustomerInfoForm;
